'use client';

import 'mapbox-gl/dist/mapbox-gl.css';

import { useEffect, useMemo, useRef } from 'react';

interface Props {
  configs: any;
  personalLocations: any;
  zoom: any;
}

interface MapOptions {
  container: any;
  style: any;
  zoom: any;
  center?: any;
}

const PersonalsLocationsView = ({
  configs,
  personalLocations,
  zoom,
}: Props) => {
  const map = useRef(null);
  const mapContainer = useRef(null);
  const mapPromise = useRef(null);

  const features = useMemo(
    () =>
      personalLocations
        .map((personalLocation) => personalLocation.children)
        .flat()
        .filter(({ count, matchCount }) => count > 0 || matchCount > 0)
        .map(({ count, location }) => ({
          type: 'Feature',
          properties: { count },
          geometry: {
            type: 'Point',
            coordinates: [location.longitude, location.latitude],
          },
        })),
    [personalLocations]
  );

  useEffect(() => {
    if (map.current) return;
    if (mapPromise.current) return;

    mapPromise.current = import('mapbox-gl').then(({ default: mapboxgl }) => {
      mapboxgl.accessToken = configs.MAPBOX_PUBLIC_KEY;

      const mapOptions: MapOptions = {
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11?optimize=true',
        zoom,
      };

      if (features.length > 0)
        mapOptions.center = features[0].geometry.coordinates;

      map.current = new mapboxgl.Map(mapOptions);
      map.current.addControl(new mapboxgl.NavigationControl());

      map.current.on('load', () => {
        map.current.addSource('personals', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features },
        });

        map.current.addLayer({
          id: 'something',
          type: 'circle',
          source: 'personals',
          paint: {
            'circle-color': '#4f5d75',
            'circle-opacity': 0.3,
            'circle-radius': 20,
          },
        });

        map.current.addLayer({
          id: 'id-labels',
          type: 'symbol',
          source: 'personals',
          layout: {
            'text-field': ['get', 'count'],
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 12,
          },
          paint: { 'text-color': 'rgba(0,0,0,0.5)' },
        });
      });

      return true;
    });
  }, [features, configs, zoom]);

  useEffect(() => {
    if (!map.current) return;
    if (!('geolocation' in navigator)) return;

    navigator.geolocation.getCurrentPosition((position) => {
      map.current.setCenter([
        position.coords.longitude,
        position.coords.latitude,
      ]);
    });
  }, []);

  useEffect(() => {
    if (!map.current) return;
    if (!map.current.loaded()) return;

    map.current
      .getSource('personals')
      .setData({ type: 'FeatureCollection', features });
  }, [features]);

  return <div ref={mapContainer} className="h-full" />;
};

export default PersonalsLocationsView;
